import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { doc, updateDoc, setDoc, collection } from 'firebase/firestore';
import { db } from '../firebase';
import './Subscription.css';

import { getDocs } from 'firebase/firestore';
import { getSubscriptionLockedFlag } from '../firestoreConfig';

const Subscription = () => {
  const [subscriptionLocked, setSubscriptionLocked] = useState(false);

  useEffect(() => {
    async function checkLocked() {
      try {
        const locked = await getSubscriptionLockedFlag();
        setSubscriptionLocked(locked);
      } catch (e) {
        setSubscriptionLocked(false);
      }
    }
    checkLocked();
  }, []);
  const { currentUser, subscriptionPlan, showToast } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [loadingStates, setLoadingStates] = useState({
    intermediate: false,
    advanced: false
  });
  const [priceLoading, setPriceLoading] = useState(true);

  const setLoading = (plan, isLoading) => {
    setLoadingStates(prev => ({
      ...prev,
      [plan]: isLoading
    }));
  };

  const PAYSTACK_PUBLIC_KEY = process.env.REACT_APP_PAYSTACK_PUBLIC_KEY;

  useEffect(() => {
    document.title = "Subscription | LightTrade FOREX Academy";
  }, [location.pathname]);

  const [intermediatePriceGHS, setIntermediatePriceGHS] = useState(null);
  const [advancedPriceGHS, setAdvancedPriceGHS] = useState(null);
  const [intermediatePriceUSD, setIntermediatePriceUSD] = useState(null);
  const [advancedPriceUSD, setAdvancedPriceUSD] = useState(null);
  const [subscriptionEligibility, setSubscriptionEligibility] = useState(null);
  const [checkingEligibility, setCheckingEligibility] = useState(false);
  const [userSubscriptionData, setUserSubscriptionData] = useState(null);

  useEffect(() => {
    const fetchPrices = async () => {
      setPriceLoading(true);
      try {
        const programsSnapshot = await getDocs(collection(db, 'programs'));
        const prices = {
          intermediate: 35, // Default fallback
          advanced: 50      // Default fallback
        };
        
        // Get prices from Firestore
        programsSnapshot.forEach(doc => {
          const data = doc.data();
          if (doc.id === 'intermediate' && data.price) {
            prices.intermediate = data.price;
          } else if (doc.id === 'advanced' && data.price) {
            prices.advanced = data.price;
          }
        });

        // Fetch exchange rate
        const rateResponse = await fetch('https://open.er-api.com/v6/latest/USD');
        const rateData = await rateResponse.json();
        if (rateData.result === 'success' && rateData.rates.GHS) {
          const rate = rateData.rates.GHS;
          setIntermediatePriceGHS(Math.round(prices.intermediate * rate));
          setAdvancedPriceGHS(Math.round(prices.advanced * rate));
          setIntermediatePriceUSD(prices.intermediate);
          setAdvancedPriceUSD(prices.advanced);
        } else {
          throw new Error('Could not fetch exchange rate.');
        }
      } catch (error) {
        console.error("Price fetch error:", error);
        showToast("Could not load subscription prices. Please try again later.", "error");
      } finally {
        setPriceLoading(false);
      }
    };

    fetchPrices();
  }, [showToast]);

  useEffect(() => {
    if (!window.PaystackPop) {
      const script = document.createElement('script');
      script.src = 'https://js.paystack.co/v1/inline.js';
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  // Check subscription eligibility on component mount
  useEffect(() => {
    const checkEligibility = async () => {
      if (!currentUser) return;
      
      setCheckingEligibility(true);
      try {
        const token = await currentUser.getIdToken();
        const response = await fetch('/api/subscription-management', {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            action: 'check_eligibility'
            // Removed userId since we get it from the token
          })
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Failed to check subscription eligibility');
        }
        
        const data = await response.json();
        setSubscriptionEligibility(data);
      } catch (error) {
        console.error('Error checking subscription eligibility:', error);
        // You might want to show this error to the user
        // setError(error.message);
      } finally {
        setCheckingEligibility(false);
      }
    };

    checkEligibility();
  }, [currentUser]);

  // Fetch user subscription data for remaining days display
  useEffect(() => {
    const fetchUserSubscriptionData = async () => {
      if (!currentUser) return;
      
      try {
        const token = await currentUser.getIdToken();
        const response = await fetch('/api/subscription-management', {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            action: 'get_subscription',
            userId: currentUser.uid
          })
        });
        
        const data = await response.json();
        setUserSubscriptionData(data);
      } catch (error) {
        console.error('Error fetching user subscription data:', error);
      }
    };

    fetchUserSubscriptionData();
  }, [currentUser]);

  // Calculate remaining days in current subscription period
  const getRemainingDays = () => {
    if (!userSubscriptionData?.currentPeriodEnd) return null;
    const now = new Date();
    const periodEnd = new Date(userSubscriptionData.currentPeriodEnd);
    const diffTime = periodEnd - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const handlePayment = (plan) => {
    if (loadingStates[plan]) return;
    setLoading(plan, true);

    // Check subscription eligibility first (billing cycle protection)
    if (subscriptionEligibility && !subscriptionEligibility.canChange) {
      showToast(subscriptionEligibility.reason || "Cannot change subscription at this time.", "error");
      setLoading(plan, false);
      return;
    }

    if (!window.PaystackPop) {
      showToast("Payment service is not available. Please refresh the page.", "error");
      setLoading(plan, false);
      return;
    }

    const planPrice = plan === 'intermediate' ? intermediatePriceGHS : advancedPriceGHS;

    if (!planPrice) {
      showToast("Cannot process payment: price not available.", "error");
      setLoading(plan, false);
      return;
    }

    const handler = window.PaystackPop.setup({
      key: PAYSTACK_PUBLIC_KEY,
      email: currentUser.email,
      amount: planPrice * 100, // Amount in kobo
      currency: 'GHS',
      ref: `LTF-${plan.toUpperCase()}-${Date.now()}`,
      metadata: {
        userId: currentUser.uid,
        plan
      },
      callback: (response) => {
        setLoading(plan, true);
        showToast('Payment completed! Verifying transaction...', 'info');

        const verifyPayment = async () => {
          try {
            // Verify and activate subscription on the server in one step
            const verifyToken = await currentUser.getIdToken();
            const verifyRes = await fetch('/api/verify-paystack', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${verifyToken}`
              },
              body: JSON.stringify({
                reference: response.reference,
                type: 'subscription',
                plan,
                userId: currentUser.uid
              })
            });
            const verifyData = await verifyRes.json();

            if (!verifyRes.ok) {
              throw new Error(verifyData.error || 'Failed to verify payment');
            }

            const paymentSuccessful =
              verifyData?.data?.status === 'success' ||
              verifyData?.status === 'success' ||
              verifyData?.status === true;

            if (paymentSuccessful) {
              showToast('Payment verified! Your plan has been upgraded.', 'success');
              
              // Send email receipt in background
              const sendEmailReceipt = async () => {
                try {
                  const emailIdToken = await currentUser.getIdToken();
                  const emailResponse = await fetch('/api/send-email-invoice', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${emailIdToken}`
                    },
                    body: JSON.stringify({
                      type: 'subscription',
                      email: currentUser.email,
                      name: currentUser.displayName || currentUser.email.split('@')[0],
                      paymentData: {
                        reference: response.reference,
                        amount: verifyData.data.amount / 100,
                        currency: verifyData.data.currency,
                        paidAt: new Date().toISOString()
                      },
                      planDetails: {
                        name: plan.charAt(0).toUpperCase() + plan.slice(1),
                        price: plan === 'intermediate' ? intermediatePriceUSD : advancedPriceUSD,
                        currency: 'USD'
                      }
                    })
                  });

                  if (!emailResponse.ok) {
                    console.error('Failed to send email receipt');
                  }
                } catch (error) {
                  console.error('Email receipt error:', error);
                }
              };

              // Don't wait for email to send
              sendEmailReceipt();
              
              // Force refresh user data
              window.location.href = '/program?payment=success';
              
            } else {
              throw new Error(verifyData.message || verifyData.error || 'Payment verification failed');
            }
          } catch (error) {
            console.error('Payment processing error:', error);
            showToast(
              `Payment completed but there was an issue: ${error.message}. ` +
              'Please contact support with reference: ' + response.reference,
              'error',
              { autoClose: 10000 }
            );
          } finally {
            setLoading(plan, false);
          }
        };

        verifyPayment();
      },
      onClose: () => {
        // Only show message if not already processing
        if (!loadingStates[plan]) {
          showToast('Payment window closed. If you completed payment, your transaction is being processed.', 'info');
        }
      },
    });

    handler.openIframe();
  };

  const PlanCard = ({ title, price, features, isCurrent, onSelect, popular = false, planId, disabled = false, subscriptionRestriction = null, ctaLabel = null }) => (
    <div className={`plan-card ${isCurrent ? 'current' : ''} ${popular ? 'popular' : ''}`}>
      {popular && <div className="popular-badge">POPULAR</div>}
      <div className="plan-card-content">
        <div className="plan-card-header">
          <h2>{title}</h2>
          <div className="price">{price}</div>
          <ul className="features">
            {features.map((feature, index) => (
              <li key={index}>{feature}</li>
            ))}
          </ul>
          {subscriptionRestriction && !subscriptionRestriction.canChange && subscriptionRestriction.daysToWait > 0 && (
            <div className="subscription-warning">
              <small style={{ color: '#e74c3c', fontSize: '12px', textAlign: 'center', display: 'block', marginBottom: '10px' }}>
                ⚠️ {subscriptionRestriction.reason}
              </small>
            </div>
          )}
        </div>
        <div className="plan-card-footer">
          {isCurrent ? (
            <button className="current-plan-btn" disabled>Current Plan</button>
          ) : (
            <button
              onClick={() => onSelect(planId)}
              className="select-plan-btn"
              disabled={loadingStates[planId] || disabled || (subscriptionRestriction && !subscriptionRestriction.canChange && subscriptionRestriction.daysToWait > 0)}
            >
              {loadingStates[planId] ? 'Processing...' : 
               subscriptionRestriction && !subscriptionRestriction.canChange && subscriptionRestriction.daysToWait > 0 ? 
               `Wait ${subscriptionRestriction.daysToWait} days` :
               (ctaLabel || (subscriptionPlan === 'legacyFree' ? 'Upgrade' : 'Change Plan'))}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  if (subscriptionLocked) {
    return (
      <div className="subscription-page">
        <div className="subscription-header">
          <h1>Subscription Plans</h1>
          <p>Choose the plan that's right for you and unlock your trading potential.</p>
          {userSubscriptionData && userSubscriptionData.status === 'active' && (
            <div className="current-subscription-info">
              <div className="subscription-status-card">
                <div className="status-info">
                  <span className="current-plan-label">Current Plan: </span>
                  <span className="current-plan-name">{userSubscriptionData.plan?.name || 'Unknown'}</span>
                </div>
                {getRemainingDays() !== null && (
                  <div className="billing-info">
                    <span className="remaining-days-label">Remaining Days: </span>
                    <span className="remaining-days-count">
                      {getRemainingDays() === 0 ? 'Expires Today' : 
                       getRemainingDays() === 1 ? '1 day' : 
                       `${getRemainingDays()} days`}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="plans-container">
          <PlanCard
            planId="free"
            title="Free"
            price={
              <div>
                <div className="price-main">$0/month</div>
                <div className="price-sub">(₵0)</div>
              </div>
            }
            features={[
              'Access to Beginner course',
              'Basic trading resources',
              'Community forum access',
              'Email support',
            ]}
            isCurrent={subscriptionPlan === 'legacyFree'}
            onSelect={() => {}}
          />
          <PlanCard
            planId="intermediate"
            title="Intermediate"
            price={
              priceLoading ? "Loading..." : intermediatePriceGHS ? (
                <div>
                  <div className="price-main">${intermediatePriceUSD}/month</div>
                  <div className="price-sub">(₵{intermediatePriceGHS})</div>
                </div>
              ) : "Price unavailable"
            }
            features={[
              'Access to Beginner + Intermediate courses',
              'Advanced Chart Patterns',
              'Risk Management Strategies',
              'Trading Psychology',
              'Market Structure Analysis',
              'Email & chat support',
            ]}
            isCurrent={subscriptionPlan === 'intermediate'}
            onSelect={() => handlePayment('intermediate')}
            disabled={priceLoading || !intermediatePriceGHS}
            subscriptionRestriction={subscriptionEligibility && !subscriptionEligibility.canChange ? subscriptionEligibility : null}
          />
          <PlanCard
            planId="advanced"
            title="Advanced"
            price={
              priceLoading ? "Loading..." : advancedPriceGHS ? (
                <div>
                  <div className="price-main">${advancedPriceUSD}/month</div>
                  <div className="price-sub">(₵{advancedPriceGHS})</div>
                </div>
              ) : "Price unavailable"
            }
            features={[
              'Access to all courses (Beginner, Intermediate, Advanced)',
              'Professional Trading Strategies',
              'Algorithmic Trading Basics',
              'Portfolio Management',
              '1-on-1 Mentorship Sessions',
              'Priority support & trade analysis',
            ]}
            isCurrent={subscriptionPlan === 'advanced'}
            onSelect={() => handlePayment('advanced')}
            popular={true}
            disabled={priceLoading || !advancedPriceGHS}
            subscriptionRestriction={subscriptionEligibility && !subscriptionEligibility.canChange ? subscriptionEligibility : null}
          />
        </div>

        {currentUser && (
          <div className="back-to-program-section">
            <button onClick={() => navigate('/program')} className="back-to-program-btn">
              Go to My Program
            </button>
          </div>
        )}
      </div>
    );
  }
  
  return (
    <div className="subscription-page">
      <div className="subscription-header">
        <h1>Subscription Plans</h1>
        <p>Choose the plan that's right for you and unlock your trading potential.</p>
        {userSubscriptionData && userSubscriptionData.status === 'active' && (
          <div className="current-subscription-info">
            <div className="subscription-status-card">
              <div className="status-info">
                <span className="current-plan-label">Current Plan: </span>
                <span className="current-plan-name">{userSubscriptionData.plan?.name || 'Unknown'}</span>
              </div>
              {getRemainingDays() !== null && (
                <div className="billing-info">
                  <span className="remaining-days-label">Remaining Days: </span>
                  <span className="remaining-days-count">
                    {getRemainingDays() === 0 ? 'Expires Today' : 
                     getRemainingDays() === 1 ? '1 day' : 
                     `${getRemainingDays()} days`}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="plans-container">
        <PlanCard
          planId="free"
          title="Free"
          price={
            <div>
              <div className="price-main">$0/month</div>
              <div className="price-sub">(₵0)</div>
            </div>
          }
          features={[
            'Access to Beginner course',
            'Basic trading resources',
            'Community forum access',
            'Email support',
          ]}
          isCurrent={subscriptionPlan === 'legacyFree'}
          onSelect={() => {}}
        />
        <PlanCard
          planId="intermediate"
          title="Intermediate"
          price={
            priceLoading ? "Loading..." : intermediatePriceGHS ? (
              <div>
                <div className="price-main">${intermediatePriceUSD}/month</div>
                <div className="price-sub">(₵{intermediatePriceGHS})</div>
              </div>
            ) : "Price unavailable"
          }
          features={[
            'Access to Beginner + Intermediate courses',
            'Advanced Chart Patterns',
            'Risk Management Strategies',
            'Trading Psychology',
            'Market Structure Analysis',
            'Email & chat support',
          ]}
          isCurrent={subscriptionPlan === 'intermediate'}
          onSelect={() => handlePayment('intermediate')}
          disabled={priceLoading || !intermediatePriceGHS}
          subscriptionRestriction={subscriptionEligibility && !subscriptionEligibility.canChange ? subscriptionEligibility : null}
        />
        <PlanCard
          planId="advanced"
          title="Advanced"
          price={
            priceLoading ? "Loading..." : advancedPriceGHS ? (
              <div>
                <div className="price-main">${advancedPriceUSD}/month</div>
                <div className="price-sub">(₵{advancedPriceGHS})</div>
              </div>
            ) : "Price unavailable"
          }
          features={[
            'Access to all courses (Beginner, Intermediate, Advanced)',
            'Professional Trading Strategies',
            'Algorithmic Trading Basics',
            'Portfolio Management',
            '1-on-1 Mentorship Sessions',
            'Priority support & trade analysis',
          ]}
          isCurrent={subscriptionPlan === 'advanced'}
          onSelect={() => handlePayment('advanced')}
          popular={true}
          disabled={priceLoading || !advancedPriceGHS}
          subscriptionRestriction={subscriptionEligibility && !subscriptionEligibility.canChange ? subscriptionEligibility : null}
        />
      </div>

      {currentUser && (
        <div className="back-to-program-section">
          <button onClick={() => navigate('/program')} className="back-to-program-btn">
            Go to My Program
          </button>
        </div>
      )}
    </div>
  );
};

export default Subscription;
